/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Virtual appointment conversion handlers for ViewAppointments.
 * @module pages/viewAppointments/useViewAppointmentsVirtualHandlers
 */

import { useCallback } from "react";
import { logger, sanitizeError } from "../../utils/logger.js";
import { proposeVirtualAppointment } from "../../logic/appointments/virtualAppointmentProposal.js";
import { applyVirtualAppointmentInCollections } from "../../logic/appointments/appointmentStateUpdates.js";
import { canMakeVirtualAppointment } from "../../logic/appointments/virtualAppointmentEligibility.js";
import {
  getSyncVirtualAppointmentBlockReason,
  loadVirtualAppointmentCandidate,
} from "../../logic/appointments/loadVirtualAppointmentCandidate.js";
import { scheduleSuccessMessageHide } from "./viewAppointmentsSuccessMessage.js";

/**
 * @param {Object} ctx - ViewAppointments context
 * @returns {Object} Virtual appointment handlers
 */
const useViewAppointmentsVirtualHandlers = (ctx) => {
  const {
    t,
    navigate,
    eventData,
    appointments,
    selectedAppointmentForVirtual,
    setActiveDropdownId,
    setIsLoading,
    setVirtualAppointmentError,
    setShowVirtualAppointmentErrorModal,
    setSelectedAppointmentForVirtual,
    setShowVirtualMeetingModal,
    setIsConvertingToVirtual,
    setShowSuccessMessage,
    setIsSlidingUp,
    setAppointments,
    setFilteredRequests,
  } = ctx;

  const canMakeVirtual = useCallback(
    (request) => canMakeVirtualAppointment(request, appointments),
    [appointments],
  );

  const showVirtualAppointmentBlockError = useCallback(
    (reason) => {
      const errorKeys = {
        not_approved: "pages.viewAppointments.virtualAppointmentMustBeApproved",
        below_threshold: "pages.viewAppointments.virtualAppointment90PercentError",
        missing_email: "pages.viewAppointments.virtualAppointmentErrorTitle",
      };
      setVirtualAppointmentError(t(errorKeys[reason] || errorKeys.missing_email));
      setShowVirtualAppointmentErrorModal(true);
    },
    [t, setVirtualAppointmentError, setShowVirtualAppointmentErrorModal],
  );

  const handleMakeVirtual = useCallback(
    async (request) => {
      setActiveDropdownId(null);

      const syncBlockReason = getSyncVirtualAppointmentBlockReason(request);
      if (syncBlockReason) {
        showVirtualAppointmentBlockError(syncBlockReason);
        return;
      }

      setIsLoading(true);
      try {
        const result = await loadVirtualAppointmentCandidate({
          navigate,
          request,
          eventId: eventData._id,
        });

        if (!result.ok) {
          showVirtualAppointmentBlockError(result.reason);
          return;
        }

        setSelectedAppointmentForVirtual(result.appointment);
        setShowVirtualMeetingModal(true);
      } catch (error) {
        logger.error("Error preparing virtual meeting:", {
          err: sanitizeError(error),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      setActiveDropdownId,
      showVirtualAppointmentBlockError,
      setIsLoading,
      navigate,
      eventData,
      setSelectedAppointmentForVirtual,
      setShowVirtualMeetingModal,
    ],
  );

  const handleVirtualAppointmentConfirm = useCallback(
    async (virtualMeetingData) => {
      if (!selectedAppointmentForVirtual) {
        return;
      }

      try {
        setIsConvertingToVirtual(true);
        const updatedAppointment = await proposeVirtualAppointment({
          eventId: eventData._id,
          appointmentId: selectedAppointmentForVirtual._id,
          virtualMeetingData,
        });

        applyVirtualAppointmentInCollections(
          selectedAppointmentForVirtual._id,
          updatedAppointment,
          {
            setAppointments,
            setFilteredRequests,
          },
        );

        setShowSuccessMessage(true);
        scheduleSuccessMessageHide({
          setSlidingUp: setIsSlidingUp,
          setVisible: setShowSuccessMessage,
        });
        setShowVirtualMeetingModal(false);
        setSelectedAppointmentForVirtual(null);
      } catch (error) {
        logger.error("Error proposing virtual appointment:", {
          err: sanitizeError(error),
        });
        setVirtualAppointmentError(
          error.message ||
            t("pages.viewAppointments.virtualAppointmentProposalFailed"),
        );
        setShowVirtualAppointmentErrorModal(true);
      } finally {
        setIsConvertingToVirtual(false);
      }
    },
    [
      selectedAppointmentForVirtual,
      setIsConvertingToVirtual,
      eventData,
      setAppointments,
      setFilteredRequests,
      setShowSuccessMessage,
      setIsSlidingUp,
      setShowVirtualMeetingModal,
      setSelectedAppointmentForVirtual,
      setVirtualAppointmentError,
      t,
      setShowVirtualAppointmentErrorModal,
    ],
  );

  const handleVirtualAppointmentCancel = useCallback(() => {
    setShowVirtualMeetingModal(false);
    setSelectedAppointmentForVirtual(null);
  }, [setShowVirtualMeetingModal, setSelectedAppointmentForVirtual]);

  const handleVirtualAppointmentErrorClose = useCallback(() => {
    setShowVirtualAppointmentErrorModal(false);
    setVirtualAppointmentError("");
  }, [setShowVirtualAppointmentErrorModal, setVirtualAppointmentError]);

  return {
    canMakeVirtual,
    handleMakeVirtual,
    handleVirtualAppointmentConfirm,
    handleVirtualAppointmentCancel,
    handleVirtualAppointmentErrorClose,
  };
};

export default useViewAppointmentsVirtualHandlers;
